/**
 * 📷 실사 사진 — 폰 사진을 **올리기 전에 줄인다** (v2.224.0 · 2026-10-01)
 *
 * 폰 원본은 3~6MB 다. NAS 에 그대로 쌓이면 분기마다 수 GB 가 되고, 보여 줄 때마다 NAS 왕복이 그만큼 느리다.
 * 긴 변 1600px(회의록 그림과 같은 기준 — 박스 라벨 글자가 읽힌다) JPEG 로 다시 그려 보통 0.2~0.5MB 가 된다.
 * 캔버스로 다시 그리므로 EXIF(위치 정보 등)도 떨어진다.
 *
 * ⚠`lib/paste-image.ts`(회의록)와 비슷하지만 **따로 둔다** — 그쪽은 dataURL(본문 삽입)을 돌려주고 PNG 를 선호한다.
 *   사진은 Blob(multipart) 이고 늘 JPEG 다. 공용 함수를 고치면 회의록이 함께 갈린다.
 */

export const PHOTO_LONG_EDGE = 1600;
export const PHOTO_MAX_BYTES = 4 * 1024 * 1024;

export interface PreparedPhoto {
  blob: Blob;
  width: number;
  height: number;
  bytes: number;
  /** 줄였나(원본이 더 컸나) */
  shrunk: boolean;
}

async function decode(file: Blob): Promise<{ src: CanvasImageSource; w: number; h: number; close: () => void }> {
  if (typeof createImageBitmap === 'function') {
    try {
      // 사진의 EXIF 회전을 적용해서 읽는다 — 안 그러면 세로로 찍은 사진이 누워서 들어간다
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
      return { src: bmp, w: bmp.width, h: bmp.height, close: () => bmp.close() };
    } catch {
      /* 아래 <img> 로 */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode-failed'));
      el.src = url;
    });
    return { src: img, w: img.naturalWidth, h: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

function draw(src: CanvasImageSource, w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('이 브라우저에서는 사진을 넣을 수 없습니다');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(src, 0, 0, w, h);
  return canvas;
}

/** 사진 한 장을 올릴 모양으로 — 못 쓰는 파일이면 사람이 읽을 이유를 던진다 */
export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  if (file.type && !file.type.startsWith('image/')) {
    throw new Error('사진 파일만 올릴 수 있습니다');
  }
  let d: Awaited<ReturnType<typeof decode>>;
  try {
    d = await decode(file);
  } catch {
    throw new Error('사진을 읽지 못했습니다 — 다시 찍어 주세요');
  }
  try {
    if (d.w <= 0 || d.h <= 0) throw new Error('사진을 읽지 못했습니다');
    const long = Math.max(d.w, d.h);
    const scale = long > PHOTO_LONG_EDGE ? PHOTO_LONG_EDGE / long : 1;
    let w = Math.max(1, Math.round(d.w * scale));
    let h = Math.max(1, Math.round(d.h * scale));
    let blob = await toBlob(draw(d.src, w, h), 0.82);
    let guard = 0;
    // 그래도 크면 점점 줄인다(아주 복잡한 사진) — 여기서 포기하면 사람은 「왜 안 되는지」만 듣는다
    while (blob && blob.size > PHOTO_MAX_BYTES && guard < 4) {
      guard += 1;
      w = Math.max(1, Math.round(w * 0.75));
      h = Math.max(1, Math.round(h * 0.75));
      blob = await toBlob(draw(d.src, w, h), 0.78);
    }
    if (!blob) throw new Error('사진을 만들지 못했습니다 — 다시 시도해 주세요');
    if (blob.size > PHOTO_MAX_BYTES) throw new Error('사진이 너무 큽니다 — 다시 찍어 주세요');
    return { blob, width: w, height: h, bytes: blob.size, shrunk: scale < 1 || guard > 0 };
  } finally {
    d.close();
  }
}

/** 「1600×1200 · 380KB」 */
export function describePhoto(p: PreparedPhoto): string {
  const kb = Math.max(1, Math.round(p.bytes / 1024));
  return `${p.width}×${p.height} · ${kb >= 1024 ? `${(kb / 1024).toFixed(1)}MB` : `${kb}KB`}`;
}
